import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, LoaderCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) return setState("missing");
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          const service = !(data.lookup_key || "organizer_lifetime").startsWith("organizer");
          setPlan(service ? data.plan : null);
          await refreshUser();
          setState("paid");
          setTimeout(() => navigate(service ? "/onboarding" : "/organizer"), 2200);
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch {
        if (cancelled) return;
      }
      if (attempts < MAX_POLLS) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate, refreshUser]);

  const copy = {
    checking: { icon: LoaderCircle, spin: true, title: "Confirming your payment…", text: "Give us a moment while we talk to Stripe. Don't close this tab." },
    paid: plan
      ? { icon: CheckCircle2, title: "You're booked.", text: `${plan} is confirmed and a receipt is on its way. Next: a 5-minute brand-assets form so we can start right away — opening it now.` }
      : { icon: CheckCircle2, title: "You're in.", text: "Payment confirmed — your organizer is unlocked. Opening your workspace now." },
    failed: { icon: XCircle, title: "That payment didn't go through.", text: "Your card wasn't charged. Head back to try again." },
    timeout: { icon: LoaderCircle, title: "Still confirming.", text: "Stripe is taking longer than usual. Your access unlocks automatically once it clears — refresh in a minute or open your organizer." },
    missing: { icon: XCircle, title: "No checkout found.", text: "We couldn't find a payment session. Start again from The Organizer." },
  }[state];

  return (
    <main className="min-h-screen vector-grid flex items-center justify-center px-6 pt-16" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl max-w-md w-full p-10 text-center">
        <copy.icon size={40} className={`mx-auto mb-5 ${state === "paid" ? "text-[#C48A9F]" : state === "failed" || state === "missing" ? "text-[#A8785A]" : "text-[#D4A0B4]"} ${copy.spin ? "animate-spin" : ""}`} />
        <span className="eyebrow">Checkout</span>
        <h1 className="f-display text-3xl font-medium tracking-tight mt-3 mb-3" data-testid="payment-status-title">{copy.title}</h1>
        <p className="text-sm text-[#8C7562] leading-relaxed mb-8" data-testid="payment-status-text">{copy.text}</p>
        <Link to={plan ? "/onboarding" : "/organizer"} className="btn-primary justify-center w-full" data-testid="payment-open-organizer-btn">
          {plan ? "Start the brand-assets form" : "Open The Organizer"} <ArrowRight size={15} />
        </Link>
      </motion.div>
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main className="min-h-screen vector-grid flex items-center justify-center px-6 pt-16" data-testid="payment-cancel-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl max-w-md w-full p-10 text-center">
        <XCircle size={40} className="mx-auto mb-5 text-[#A8785A]" />
        <span className="eyebrow">Checkout cancelled</span>
        <h1 className="f-display text-3xl font-medium tracking-tight mt-3 mb-3">No charge was made.</h1>
        <p className="text-sm text-[#8C7562] leading-relaxed mb-8">
          Nothing was charged. Whenever you're ready, The Organizer and every social media plan are one click away.
        </p>
        <Link to="/organizer" className="btn-primary justify-center w-full" data-testid="payment-cancel-back-btn">
          Back to The Organizer <ArrowRight size={15} />
        </Link>
      </motion.div>
    </main>
  );
}
